/**
 * 📊 CHIFFRES DE LA PLATEFORME (source unique)
 * ===========================================
 * Tous les chiffres publics du site (accueil, statistiques, bandeau) viennent d'ici :
 * ils sont calculés à partir des projets publiés et des paiements CONFIRMÉS, jamais saisis à la main.
 *
 * - Supabase configuré : fonction SQL public.platform_stats() (database/supabase-schema.sql),
 *   qui agrège les tables projects et investments côté serveur.
 * - Démonstration : catalogue des projets + investissements confirmés enregistrés dans ce navigateur.
 *
 * Les montants sont en FCFA. Les rendements sont des rendements VISÉS (non garantis).
 */

import { useEffect, useState } from 'react';
import { supabase, isDevelopmentMode } from '../utils/supabase/client';
import { getProjects as getStaticProjects } from '../data/projects';

const DEMO_INVESTMENTS_KEY = 'demo_investments';
export const DEMO_NEWSLETTER_KEY = 'demo_newsletter_subscribers';

// Fréquence de mise à jour automatique des chiffres (paiements confirmés par d'autres visiteurs)
const REFRESH_INTERVAL_MS = 60 * 1000;

const readDemoInvestments = () => {
  try {
    return JSON.parse(localStorage.getItem(DEMO_INVESTMENTS_KEY) || '[]');
  } catch (error) {
    return [];
  }
};

const readDemoNewsletterCount = () => {
  try {
    return JSON.parse(localStorage.getItem(DEMO_NEWSLETTER_KEY) || '[]').length;
  } catch (error) {
    return 0;
  }
};

const cityOf = (project) => String(project.location || '').split(',')[0].trim() || null;

/**
 * Démonstration : ajoute aux projets du catalogue les investissements confirmés
 * (montant collecté et nombre d'investisseurs), comme le fait le trigger SQL en production.
 */
export function applyPlatformActivity(projects, investments = readDemoInvestments()) {
  if (!isDevelopmentMode) return projects;
  const confirmed = investments.filter(inv => inv.payment_status === 'completed');
  if (confirmed.length === 0) return projects;

  return projects.map(project => {
    const mine = confirmed.filter(inv => String(inv.project_id) === String(project.id));
    if (mine.length === 0) return project;
    const newInvestors = new Set(mine.map(inv => inv.investor_id)).size;
    return {
      ...project,
      montantRecu: (Number(project.montantRecu) || 0) + mine.reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0),
      membres: (Number(project.membres) || 0) + newInvestors
    };
  });
}

const groupBy = (projects, keyOf) => {
  const groups = new Map();
  projects.forEach(project => {
    const key = keyOf(project);
    if (!key) return;
    const group = groups.get(key) || { name: key, count: 0, raised: 0, target: 0 };
    group.count += 1;
    group.raised += Number(project.montantRecu) || 0;
    group.target += Number(project.coutTotal) || 0;
    groups.set(key, group);
  });
  return [...groups.values()].sort((a, b) => b.raised - a.raised);
};

/**
 * Calcule les chiffres à partir d'une liste de projets (format du catalogue) et d'investissements confirmés.
 */
export function computePlatformStats(projects, confirmedInvestments = [], extra = {}) {
  const target = p => Number(p.coutTotal) || 0;
  const raised = p => Number(p.montantRecu) || 0;
  const totalTarget = projects.reduce((sum, p) => sum + target(p), 0);
  const totalRaised = projects.reduce((sum, p) => sum + raised(p), 0);
  const rated = projects.filter(p => Number(p.tauxRetour) > 0);
  const ratedTarget = rated.reduce((sum, p) => sum + target(p), 0);
  const withImpact = projects.filter(p => Number(p.impact?.beneficiaires) > 0);
  const durations = projects.map(p => parseInt(p.duree, 10)).filter(months => months > 0);

  const monthly = new Map();
  confirmedInvestments.forEach(inv => {
    const month = String(inv.payment_date || inv.created_at || '').slice(0, 7);
    if (!month) return;
    const entry = monthly.get(month) || { month, count: 0, amount: 0 };
    entry.count += 1;
    entry.amount += Number(inv.amount) || 0;
    monthly.set(month, entry);
  });

  // Investisseurs : une personne qui investit dans plusieurs projets ne compte qu'une fois.
  // « membres » de chaque projet contient déjà ses nouveaux investisseurs (applyPlatformActivity) :
  // on retire ces comptes par projet et on ajoute le nombre de personnes distinctes.
  const investorsByProject = new Map();
  const distinctInvestors = new Set();
  confirmedInvestments.forEach(inv => {
    if (!inv.investor_id) return;
    const key = String(inv.project_id);
    if (!investorsByProject.has(key)) investorsByProject.set(key, new Set());
    investorsByProject.get(key).add(inv.investor_id);
    distinctInvestors.add(inv.investor_id);
  });
  const countedPerProject = [...investorsByProject.values()].reduce((sum, ids) => sum + ids.size, 0);
  const membersTotal = projects.reduce((sum, p) => sum + (Number(p.membres) || 0), 0);

  return {
    projectsCount: projects.length,
    openProjects: projects.filter(p => !target(p) || raised(p) < target(p)).length,
    fundedProjects: projects.filter(p => target(p) > 0 && raised(p) >= target(p)).length,
    totalTarget,
    totalRaised,
    averageRaised: projects.length ? Math.round(totalRaised / projects.length) : 0,
    fundingRate: totalTarget > 0 ? Math.round((totalRaised / totalTarget) * 100) : 0,
    investorsCount: Math.max(0, membersTotal - countedPerProject) + distinctInvestors.size,
    confirmedInvestments: confirmedInvestments.length,
    minReturn: rated.length ? Math.round(Math.min(...rated.map(p => Number(p.tauxRetour))) * 1000) / 10 : null,
    maxReturn: rated.length ? Math.round(Math.max(...rated.map(p => Number(p.tauxRetour))) * 1000) / 10 : null,
    weightedReturn: ratedTarget > 0
      ? Math.round((rated.reduce((sum, p) => sum + target(p) * Number(p.tauxRetour), 0) / ratedTarget) * 1000) / 10
      : null,
    beneficiariesTarget: withImpact.reduce((sum, p) => sum + Number(p.impact.beneficiaires), 0),
    beneficiariesProjects: withImpact.length,
    averageDurationMonths: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null,
    sectors: groupBy(projects, p => p.category),
    cities: groupBy(projects, cityOf),
    monthly: [...monthly.values()].sort((a, b) => a.month.localeCompare(b.month)),
    newsletterSubscribers: Number(extra.newsletterSubscribers) || 0,
    source: isDevelopmentMode ? 'demo' : 'catalogue',
    updatedAt: new Date().toISOString()
  };
}

const fromDatabase = (row) => ({
  projectsCount: Number(row.projectsCount) || 0,
  openProjects: Number(row.openProjects) || 0,
  fundedProjects: Number(row.fundedProjects) || 0,
  totalTarget: Number(row.totalTarget) || 0,
  totalRaised: Number(row.totalRaised) || 0,
  averageRaised: Number(row.projectsCount) > 0 ? Math.round(Number(row.totalRaised) / Number(row.projectsCount)) : 0,
  fundingRate: Number(row.totalTarget) > 0 ? Math.round((Number(row.totalRaised) / Number(row.totalTarget)) * 100) : 0,
  investorsCount: Number(row.investorsCount) || 0,
  confirmedInvestments: Number(row.confirmedInvestments) || 0,
  minReturn: row.minReturn === null ? null : Number(row.minReturn),
  maxReturn: row.maxReturn === null ? null : Number(row.maxReturn),
  weightedReturn: row.weightedReturn === null ? null : Number(row.weightedReturn),
  beneficiariesTarget: Number(row.beneficiariesTarget) || 0,
  beneficiariesProjects: Number(row.beneficiariesProjects) || 0,
  averageDurationMonths: row.averageDurationMonths ? Number(row.averageDurationMonths) : null,
  sectors: (row.sectors || []).map(s => ({ ...s, count: Number(s.count), raised: Number(s.raised), target: Number(s.target) })),
  cities: (row.cities || []).map(c => ({ ...c, count: Number(c.count), raised: Number(c.raised), target: Number(c.target) })),
  monthly: (row.monthly || []).map(m => ({ ...m, count: Number(m.count), amount: Number(m.amount) })),
  newsletterSubscribers: Number(row.newsletterSubscribers) || 0,
  source: 'live',
  updatedAt: new Date().toISOString()
});

/**
 * Chiffres à jour de la plateforme.
 */
export async function getPlatformStats() {
  if (!isDevelopmentMode) {
    try {
      const { data, error } = await supabase.rpc('platform_stats');
      if (error) throw error;
      if (data) return fromDatabase(data);
    } catch (error) {
      console.warn('Statistiques Supabase indisponibles, calcul depuis le catalogue :', error.message);
    }
  }

  return computeLocalStats();
}

// Calcul local (démonstration, ou repli si Supabase ne répond pas)
function computeLocalStats() {
  const investments = isDevelopmentMode ? readDemoInvestments() : [];
  return computePlatformStats(
    applyPlatformActivity(getStaticProjects(), investments),
    investments.filter(inv => inv.payment_status === 'completed'),
    { newsletterSubscribers: isDevelopmentMode ? readDemoNewsletterCount() : 0 }
  );
}

// Événements qui déclenchent un nouveau calcul immédiat
const REFRESH_EVENTS = ['investment-updated', 'projects-updated', 'newsletter-updated', 'storage', 'focus'];

/**
 * Hook React : chiffres de la plateforme, toujours à jour.
 * - calcul immédiat au premier affichage (pas d'écran vide)
 * - recalcul à chaque paiement confirmé, projet ajouté ou inscription à la newsletter (cet onglet ou un autre)
 * - actualisation automatique chaque minute tant que la page est visible, et au retour sur l'onglet
 */
export function usePlatformStats() {
  const [stats, setStats] = useState(computeLocalStats);

  useEffect(() => {
    let active = true;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      getPlatformStats().then(next => { if (active) setStats(next); });
    };
    refresh();
    const timer = setInterval(refresh, REFRESH_INTERVAL_MS);
    REFRESH_EVENTS.forEach(name => window.addEventListener(name, refresh));
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      clearInterval(timer);
      REFRESH_EVENTS.forEach(name => window.removeEventListener(name, refresh));
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  return stats;
}

// Formats d'affichage partagés
export const formatCount = (value) => Math.round(Number(value) || 0).toLocaleString('fr-FR');

/** 1 250 000 → "1,25 M FCFA" ; 76 500 → "76 500 FCFA" */
export const formatXofCompact = (value) => {
  const amount = Number(value) || 0;
  if (amount >= 1e9) return `${(amount / 1e9).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} Md FCFA`;
  if (amount >= 1e6) return `${(amount / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} M FCFA`;
  return `${Math.round(amount).toLocaleString('fr-FR')} FCFA`;
};

export const formatRate = (value) =>
  (value === null || value === undefined ? '—' : `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`);

export const formatReturnRange = (stats) => {
  if (stats.minReturn === null || stats.maxReturn === null) return '—';
  if (stats.minReturn === stats.maxReturn) return formatRate(stats.minReturn);
  return `${stats.minReturn.toLocaleString('fr-FR')} à ${formatRate(stats.maxReturn)}`;
};

/**
 * Mesures publiques, définies une seule fois : valeur affichée + libellé exact.
 * Utilisation dans une page : const platform = usePlatformStats(); METRICS.investors.value(platform)
 */
export const METRICS = {
  totalRaised: { label: 'FCFA collectés', value: s => formatXofCompact(s.totalRaised) },
  averageRaised: { label: 'collectés en moyenne par projet', value: s => formatXofCompact(s.averageRaised) },
  fundingRate: { label: "de l'objectif de collecte atteint", value: s => `${s.fundingRate} %` },
  investors: { label: 'investisseurs', value: s => formatCount(s.investorsCount) },
  projects: { label: 'projets publiés', value: s => formatCount(s.projectsCount) },
  projectOwners: { label: 'porteurs de projet accompagnés', value: s => formatCount(s.projectsCount) },
  openProjects: { label: 'projets ouverts', value: s => formatCount(s.openProjects) },
  fundedProjects: { label: 'projets financés à 100 %', value: s => formatCount(s.fundedProjects) },
  beneficiaries: { label: 'bénéficiaires visés', value: s => formatCount(s.beneficiariesTarget) },
  cities: { label: 'villes couvertes', value: s => formatCount(s.cities.length) },
  sectors: { label: "secteurs d'activité", value: s => formatCount(s.sectors.length) },
  weightedReturn: { label: 'rendement annuel visé (non garanti)', value: s => formatRate(s.weightedReturn) },
  confirmedInvestments: { label: 'paiements confirmés', value: s => formatCount(s.confirmedInvestments) },
  newsletterSubscribers: { label: 'abonnés à la newsletter', value: s => formatCount(s.newsletterSubscribers) },
  averageDuration: { label: 'mois de durée moyenne des projets', value: s => (s.averageDurationMonths ? formatCount(s.averageDurationMonths) : '—') }
};

/**
 * Nombre de projets publiés dont la description ou le public visé correspond au motif
 * (ex. /femme/i pour les projets dédiés aux femmes). Calculé sur le catalogue des projets.
 */
export const countProjectsMatching = (pattern) =>
  getStaticProjects().filter(p => pattern.test([p.titre, p.description, p.beneficiaires, p.impactResume].join(' '))).length;
